export interface EmailComposition {
    to?: string;
    subject?: string;
    body?: string;
}

export type EmailProvider = "gmail" | "outlook-live" | "outlook" | "office" | "mailto";

/**
 * Generate email compose URL based on the provider and email details
 */
export const generateEmailComposeUrl = (
    provider: EmailProvider,
    emailDetails: EmailComposition
): string => {
    const { to = "", subject = "", body = "" } = emailDetails;

    switch (provider) {
        case "gmail":
            const gmailParams = new URLSearchParams({
                to,
                su: subject,
                body,
            });
            return `https://mail.google.com/mail/?view=cm&fs=1&tf=1&${gmailParams.toString()}`;

        case "outlook-live":
            const outlookLiveParamPairs = [];
            if (to) outlookLiveParamPairs.push(`to=${encodeURIComponent(to)}`);
            if (subject) outlookLiveParamPairs.push(`subject=${encodeURIComponent(subject)}`);
            if (body) outlookLiveParamPairs.push(`body=${encodeURIComponent(body)}`);
            return `https://outlook.live.com/mail/0/deeplink/compose?${outlookLiveParamPairs.join('&')}`;

        case "outlook":
        case "office":
            // Outlook Office 365
            const officeParams = new URLSearchParams({
                to,
                subject,
                body,
            });
            return `https://outlook.office.com/mail/deeplink/compose?${officeParams.toString()}`;

        case "mailto":
        default:
            // Default system email client
            const mailtoParams = new URLSearchParams();
            if (subject) mailtoParams.set("subject", subject);
            if (body) mailtoParams.set("body", body);
            
            const mailtoUrl = `mailto:${to}`;
            const paramsString = mailtoParams.toString();
            return paramsString ? `${mailtoUrl}?${paramsString}` : mailtoUrl;
    }
};

/**
 * Generate email search URL for providers that support it
 */
export const generateEmailSearchUrl = (
    provider: EmailProvider,
    query: string
): string | null => {
    switch (provider) {
        case "gmail":
            return `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(query)}`;

        case "outlook-live":
            return `https://outlook.live.com/mail/0/search?q=${encodeURIComponent(query)}`;

        case "outlook":
        case "office":
            return `https://outlook.office.com/mail/search?q=${encodeURIComponent(query)}`;

        case "mailto":
        default:
            // Mailto doesn't support search
            return null;
    }
};

/**
 * Get user's email provider preference from their user data
 */
export const getUserEmailProvider = async (): Promise<EmailProvider> => {
    try {
        // Import here to avoid circular dependencies
        const { getMyInfo } = await import("../interfaces/user");
        const res = await getMyInfo();
        return res?.data?.emailProvider || "gmail";
    } catch (error) {
        console.warn("Failed to get user email provider, defaulting to gmail:", error);
        return "gmail";
    }
};

/**
 * Get a human-readable name for the email provider
 */
export const getEmailProviderDisplayName = (provider: EmailProvider): string => {
    switch (provider) {
        case "gmail":
            return "Gmail";
        case "outlook-live":
            return "Outlook (Live/Hotmail)";
        case "outlook":
            return "Outlook (Office 365)";
        case "office":
            return "Office (Office 365)";
        case "mailto":
            return "Default Email App";
        default:
            return "Unknown Provider";
    }
};